import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../contexts/ThemeContext";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";
import "swiper/swiper.min.css";
import { FaMapMarkerAlt, FaEnvelope, FaPhone } from 'react-icons/fa';
import gmap from "../public/images/gmap.png"
import Image from 'next/image'
import { BUILDER_URL, setImageSrc } from '../utils/config';
const BASE_URL = BUILDER_URL;
import * as Sentry from "@sentry/react";
import Custom404 from '../pages/404';
import getRedisClient from '../utils/redis';
import getRedisClientLocal from '../utils/localredis';
import errorLogApi from '../components/custom/ErrorLogApi';
import {NoDataFound} from '../pages/404';
import useTracking from '../components/custom/useTracking';
/**
 * ContactUs Component
 * A React component for displaying contact information fetched from an API.
 * It uses context for theme information and Axios for API requests.
 */
const ContactUs = () => {
    // Context hook to access theme information.
  const theme = useContext(ThemeContext); 
  const [contactData, setContactData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorState, setErrorState] = useState(false);
  const trackPage = useTracking('Contact Us Page');
// Effect to fetch contact data from the API on component mount.
   useEffect(() => {
    
    axios.get(`${BASE_URL}/contact-us-page`, {
        headers: { 'Authorization': `Bearer ${theme.token}` }
      })
      .then((response) => {
        if (response.data && response.data.length > 0) {
          setContactData(response.data[0]);
          setIsLoading(false);
        }
        setErrorState(false);
      })
      .catch((error) => {
        setErrorState(true);
        setIsLoading(false);
        Sentry.captureException(error);
      });
      trackPage();
}, [theme]);
// Sorting stores based on whether they are primary.
const sortedStores = contactData?.contact_us_store.sort((a, b) => {
  if (a.is_primary === 1) return -1;
  if (b.is_primary === 1) return 1;
  return 0;
});

if(errorState) {
  return (
    <>
      <Custom404 />
    </>
  )
}
 // Return a loading message if data is still loading.
if(isLoading){
  return (

    <div className="block xl:container  mx-auto px-4 py-12 bg-gray-50 min-h-screen font-body">
    {/* Title & Description Shimmer */}
    <div className="text-center py-8">
      <div className="h-10 w-1/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-8 rounded"></div>
      <div className="h-6 w-3/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer mx-auto mb-4 rounded"></div>
    </div>
  
    {/* Stores Shimmer with square cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, idx) => (
        <div key={idx} className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow-md w-full aspect-square"> {/* Adjusted for square aspect ratio */}
          <div className="h-10 w-3/4 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer rounded mb-4"></div>
          <div className="h-6 w-5/6 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer rounded mb-2"></div>
          <div className="h-6 w-5/6 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer rounded mb-2"></div>
          <div className="h-6 w-5/6 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer rounded mb-2"></div>
          <div className="h-6 w-5/6 bg-gradient-to-r from-gray-400 to-gray-300 animate-shimmer rounded"></div>
        </div>
      ))}
    </div>
  </div>
  
  
  )
}
    return (
      <>
      
      {contactData?  <div className="mb-10 p-3 lg:mb-0">
         <section class="relative z-10 overflow-hidden bg-white py-5 lg:py-[60px]">
    
    <div class="block xl:container px-4 mx-auto">
      <div class="mx-4 flex flex-wrap md:flex-nowrap lg:justify-between">
        
        <div class="w-full px-4 ">
          
          <div class="mb-12 w-full lg:mb-0">
            <span class="text-primary mb-4 block text-2xl lg:text-3xl font-semibold">
            {contactData?.title}
            </span>
            {/* ui updated - 30/08/2024 */}
            <h2
              class="text-dark mb-6 text-[32px] font-bold uppercase sm:text-[40px] lg:text-[36px] xl:text-[40px]"
            >
              GET IN TOUCH WITH US 
            </h2>
            <p className="mb-6" dangerouslySetInnerHTML={{ __html: contactData.contact_us_page_description }}></p>
          </div>
        </div>
       
        <div className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedStores.map((store, index) => (
              <div key={index} className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow-md max-w-[500px] mx-auto md:col-span-3">
                {/* Check if Custom Description is available */}
                {store.custom_description ? (
                  <p dangerouslySetInnerHTML={{ __html: store.custom_description }}/>
                ) : (
                  <>
                    {/* Store Name */}
                    <h4 className="text-2xl font-bold">{store.store_name}</h4>

                    {/* Store Address and Location */}
                    <div className="flex items-start">
                      <FaMapMarkerAlt className="text-primary mt-[2px]" size={46} />
                      <p className="text-base ml-2">{store.store_address}</p>
                    </div>

                    {/* Google Maps Image with Link */}
                    <div>
                      <Link href={store.store_location} passHref target="_blank">
                        <div target="_blank" rel="noopener noreferrer">
                          <Image 
                            src={gmap}
                            width={150}
                            height={150}
                            alt={store.store_name} 
                            className="mt-2"
                          />
                        </div>
                      </Link>
                    </div>

                    {/* Store Phone Number */}
                    {store.store_phone_number && (
          <div className="flex flex-wrap gap-3 items-start">
            {store.store_phone_number.split(',').map((phoneNumber, index) => (
              <div key={index} className="flex items-center mr-4">
                <FaPhone className="text-primary mt-[2px]" size={20} />
                <a href={`tel:${phoneNumber.trim()}`} className="text-base ml-2">
                  (+91) {phoneNumber.trim()}
                </a>
              </div>
            ))}
          </div>
        )}


                    {/* Store Email */}
                    <div className="flex items-start">
                      <FaEnvelope className="text-primary mt-[2px]" size={20} />
                      <a href={`mailto:${store.store_email}`} className="text-base ml-2">
                        {store.store_email}
                      </a>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      
      </div>
      
    </div>
     
  </section>

    </div>:<NoDataFound/>}
      
      </>
    )
}

export async function getServerSideProps(context) {
  const { req } = context;
  // Helper function to extract the domain name
  function sanitizeHostname(host) {
    const hostname = new URL(`https://${host}`).hostname;
    return hostname;
  }

  const logError = (endpoint, error) => {
    const responseData = error.response?.data ?? '';
    errorLogApi({
      job_id: "",
      endpoint: endpoint,
      payload: '',
      responseData: responseData,
      errorMsg: error
    });
  };


  const hostname = req.headers.host === "localhost:3000" ? process.env.NEXT_PUBLIC_WEB_HOST : sanitizeHostname(req.headers.host);

  const redishostname = sanitizeHostname(req.headers.host);
  const redis = redishostname.startsWith('localhost') ? getRedisClientLocal() : getRedisClient();
  

  const previewUrl = req.headers.host === "localhost:3000"
    ? "https://preview.jpencil.in/?pid=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqYm9faWQiOjIzMywic2l0ZV9zdGF0dXMiOjAsImlhdCI6MTcxNDY0MzI4MX0.QXsuG6PL2rBZP92YZMTeMRnO3at8Sfq2gOf22rG6jeg"
    : req.url;

  const urlObj = new URL(previewUrl, `http://${hostname}`);
  const pid = urlObj.searchParams.get('pid');
  

  if (hostname.startsWith('preview.')) {
    const innerJson = JSON.stringify({
      domain: hostname,
      token: pid
    });

    const payload = {
      data: innerJson
    };

    try {
      const response = await axios.post(`https://${hostname}/api/user/getJbo`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      
      return { props: { products: response.data } };
    } catch (error) {
      // To capture logs
      logError('getJbo', error);
    }
  }

  try {

    const cachedProducts = await redis.get(hostname);


    if (cachedProducts) {
    
      return { props: { products: JSON.parse(cachedProducts) } };
    }

    // If no cache, make an API call for no cached Products
    const innerJson = JSON.stringify({ domain: hostname });
    const payload = {
      data: JSON.stringify({ domain: hostname })
    };


    const response = await axios.post(`https://${hostname}/api/user/getJbo`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.data && Object.keys(response.data).length) {
      
      return { props: { products: response.data } };
    }

    // To capture logs
    logError('getJbo', error);
    
    return { props: { products: [] } };

  } catch (error) {
    // To capture logs
    logError('getJbo', error);

    return { props: { products: [] } };
  }
}


export default ContactUs
